"use client";

import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Paintbrush, House as Home, Hammer, CircleCheck as CheckCircle2, Check } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const categories = [{
  id: 1,
  title: "Preventative Maintenance",
  icon: "ShieldCheck",
  description: "Keep your home running smoothly and prevent costly emergencies."
}, {
  id: 2,
  title: "Interior Repairs",
  icon: "Paintbrush",
  description: "Fixes and updates for the inside of your home."
}, {
  id: 3,
  title: "Exterior Maintenance",
  icon: "Home",
  description: "Protect your home's exterior from the elements."
}, {
  id: 4,
  title: "General Handyman",
  icon: "Hammer",
  description: "Versatile help for all those lingering to-do list items."
}];
const categoryItems = [{
  categoryId: 1,
  text: "Seasonal home inspection"
}, {
  categoryId: 1,
  text: "HVAC filter replacement"
}, {
  categoryId: 1,
  text: "Gutter cleaning & inspection"
}, {
  categoryId: 1,
  text: "Roof inspection & minor repairs"
}, {
  categoryId: 1,
  text: "Caulking and weatherproofing"
}, {
  categoryId: 2,
  text: "Drywall repair and painting"
}, {
  categoryId: 2,
  text: "Door and hardware repair"
}, {
  categoryId: 2,
  text: "Cabinet and countertop repairs"
}, {
  categoryId: 2,
  text: "Flooring repair and refinishing"
}, {
  categoryId: 2,
  text: "Plumbing fixture repair"
}, {
  categoryId: 3,
  text: "Roof leak detection and repair"
}, {
  categoryId: 3,
  text: "Gutter replacement and repair"
}, {
  categoryId: 3,
  text: "Siding repair and maintenance"
}, {
  categoryId: 3,
  text: "Window and door caulking"
}, {
  categoryId: 3,
  text: "Deck staining and repair"
}, {
  categoryId: 4,
  text: "Minor plumbing fixes"
}, {
  categoryId: 4,
  text: "Light electrical work"
}, {
  categoryId: 4,
  text: "Appliance repairs"
}, {
  categoryId: 4,
  text: "Weatherstripping replacement"
}, {
  categoryId: 4,
  text: "Furniture assembly"
}];
const plans = [{
  id: 1,
  name: "Monthly Maintenance",
  price: "Custom Quote",
  period: "from your pro",
  popular: false,
  description: "Essential monthly upkeep for peace of mind."
}, {
  id: 2,
  name: "Quarterly Check-Up",
  price: "Custom Quote",
  period: "from your pro",
  popular: true,
  description: "Comprehensive seasonal maintenance and tuning."
}, {
  id: 3,
  name: "Annual Optimization",
  price: "Custom Quote",
  period: "from your pro",
  popular: false,
  description: "Complete yearly overhaul and scheduled service."
}];
const planFeatures = [{
  planId: 1,
  text: "Monthly 30-minute inspection"
}, {
  planId: 1,
  text: "Filter replacement (HVAC)"
}, {
  planId: 1,
  text: "Gutter cleaning (seasonal)"
}, {
  planId: 1,
  text: "Routine repair scheduling"
}, {
  planId: 1,
  text: "Direct communication with pro"
}, {
  planId: 2,
  text: "Full home inspection"
}, {
  planId: 2,
  text: "HVAC tune-up"
}, {
  planId: 2,
  text: "Gutter cleaning"
}, {
  planId: 2,
  text: "Roof inspection"
}, {
  planId: 2,
  text: "Plumbing check"
}, {
  planId: 2,
  text: "Major repair coordination"
}, {
  planId: 3,
  text: "2 complete home inspections"
}, {
  planId: 3,
  text: "HVAC spring & fall tune-ups"
}, {
  planId: 3,
  text: "Gutter cleaning (spring & fall)"
}, {
  planId: 3,
  text: "Roof inspection"
}, {
  planId: 3,
  text: "Electrical panel review"
}, {
  planId: 3,
  text: "Flexible scheduling"
}, {
  planId: 3,
  text: "Transparent project estimate"
}];
const getIcon = (name: string) => {
  switch (name) {
    case 'ShieldCheck':
      return <ShieldCheck className="size-6 text-primary" />;
    case 'Paintbrush':
      return <Paintbrush className="size-6 text-primary" />;
    case 'Home':
      return <Home className="size-6 text-primary" />;
    case 'Hammer':
      return <Hammer className="size-6 text-primary" />;
    default:
      return <Check className="size-6 text-primary" />;
  }
};
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function PricingPackages() {
  return <section id="servicepricingpackages" className="bg-background py-24 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        
        {}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mx-auto max-w-2xl text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl font-default">
            Complete Home Maintenance Services
          </h2>
          <p className="mt-4 text-lg leading-8 text-muted-foreground">
            From preventative seasonal inspections to repairs and upgrades, local independent pros handle the projects that keep your home in top condition.
          </p>
        </motion.div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-100px"
      }} className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((category, i) => <motion.div key={category.id} variants={itemVariants} data-index={i}>
              <Card className="h-full border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
                <CardHeader>
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                    {getIcon(category.icon)}
                  </div>
                  <CardTitle className="text-xl font-default">{category.title}</CardTitle>
                  <CardDescription>{category.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    {categoryItems.filter(item => item.categoryId === category.id).map((item, j) => <li key={j} data-index={j} className="flex items-start gap-3">
                        <Check className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item.text}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <Separator className="my-20" />

        {}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mx-auto max-w-2xl text-center mb-16">
          <h3 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl font-default">
            Transparent Maintenance Packages
          </h3>
          <p className="mt-4 text-lg leading-8 text-muted-foreground">
            Choose the level of care your home needs. Regular maintenance helps prevent expensive repairs and extends system life.
          </p>
        </motion.div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-100px"
      }} className="grid grid-cols-1 gap-8 md:grid-cols-3 lg:gap-12 items-center">
          {plans.map((plan, i) => <motion.div key={plan.id} variants={itemVariants} data-index={i} className={plan.popular ? "md:-mt-8 md:mb-8" : ""}>
              <Card className={`flex flex-col h-full relative ${plan.popular ? 'border-primary shadow-lg ring-1 ring-primary/20' : 'border-border shadow-md'}`}>
                {plan.popular && <div className="absolute -top-4 left-0 right-0 flex justify-center">
                    <Badge variant="default" className="px-3 py-1 text-sm uppercase tracking-wider font-semibold shadow-sm">
                      Most Popular
                    </Badge>
                  </div>}
                <CardHeader className={plan.popular ? "pt-8" : ""}>
                  <CardTitle className="text-2xl font-default">{plan.name}</CardTitle>
                  <CardDescription className="mt-2">{plan.description}</CardDescription>
                  <div className="mt-6 flex items-baseline gap-x-2">
                    <span className="text-4xl font-bold tracking-tight text-foreground font-default">{plan.price}</span>
                    <span className="text-sm font-semibold leading-6 text-muted-foreground">{plan.period}</span>
                  </div>
                </CardHeader>
                <CardContent className="flex-1">
                  <Separator className="mb-6" />
                  <ul className="space-y-4 text-sm leading-6 text-muted-foreground">
                    {planFeatures.filter(feature => feature.planId === plan.id).map((feature, j) => <li key={j} data-index={j} className="flex gap-x-3">
                        <CheckCircle2 className="h-5 w-5 flex-none text-primary" aria-hidden="true" />
                        {feature.text}
                      </li>)}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button asChild variant={plan.popular ? "default" : "outline"} size="lg" className="w-full">
                    <Link to="#bookingrequest">
                      Request a Quote
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

      </div>
    </section>;
}